const ACCESS_TOKEN_KEY = "accessToken";
const AUTH_USER_KEY = "authUser";
const REMEMBER_ME_KEY = "rememberMe";
const REMEMBERED_EMAIL_KEY = "rememberedEmail";

const parseJwtPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const normalizeRoles = (roles) => {
  if (!roles) {
    return [];
  }

  return (Array.isArray(roles) ? roles : [roles])
    .filter(Boolean)
    .map((role) => String(role).toUpperCase());
};

export const buildUserFromAuthResponse = (response, email) => {
  const token = response?.accessToken || response?.token;
  const jwtPayload = token ? parseJwtPayload(token) : null;
  const apiUser = response?.user || response?.data?.user || {};
  const roles = normalizeRoles(
    apiUser.roles ||
      apiUser.role ||
      jwtPayload?.rol ||
      jwtPayload?.roles ||
      jwtPayload?.role,
  );

  return {
    name:
      apiUser.name ||
      apiUser.fullName ||
      jwtPayload?.name ||
      email?.split("@")[0] ||
      "User",
    email: apiUser.email || jwtPayload?.email || email,
    roles,
    pictureUrl:
      apiUser.pictureUrl ||
      apiUser.picture ||
      apiUser.photoUrl ||
      jwtPayload?.picture ||
      null,
    googleId: apiUser.googleId || apiUser.sub || jwtPayload?.googleId || null,
  };
};

const getActiveStorage = () => {
  if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) {
    return sessionStorage;
  }

  if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
    return localStorage;
  }

  return null;
};

export const getAccessToken = () => {
  const storage = getActiveStorage();
  return storage?.getItem(ACCESS_TOKEN_KEY) || null;
};

export const getAuthUser = () => {
  const storage = getActiveStorage();

  if (!storage) {
    return null;
  }

  try {
    const user = JSON.parse(storage.getItem(AUTH_USER_KEY) || "null");
    return user && typeof user === "object" ? user : null;
  } catch {
    return null;
  }
};

export const isLoggedIn = () => Boolean(getAccessToken() && getAuthUser());

const getRolesFromToken = (token) => {
  const payload = parseJwtPayload(token);

  return normalizeRoles(payload?.rol || payload?.roles || payload?.role);
};

export const isAdminUser = () => {
  const user = getAuthUser();
  const rolesFromUser = normalizeRoles(user?.roles);

  if (rolesFromUser.includes("ADMIN")) {
    return true;
  }

  const token = getAccessToken();

  if (!token) {
    return false;
  }

  return getRolesFromToken(token).includes("ADMIN");
};

export const getRememberedEmail = () =>
  localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";

export const setAuthSession = ({ token, user }, rememberMe = false) => {
  clearAuthSession();

  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem(ACCESS_TOKEN_KEY, token);
  storage.setItem(AUTH_USER_KEY, JSON.stringify(user));

  if (rememberMe) {
    localStorage.setItem(REMEMBER_ME_KEY, "true");
    localStorage.setItem(REMEMBERED_EMAIL_KEY, user?.email || "");
  } else {
    localStorage.removeItem(REMEMBER_ME_KEY);
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  }

  window.dispatchEvent(new Event("auth-changed"));
};

export const clearAuthSession = () => {
  [localStorage, sessionStorage].forEach((storage) => {
    storage.removeItem(ACCESS_TOKEN_KEY);
    storage.removeItem(AUTH_USER_KEY);
  });

  window.dispatchEvent(new Event("auth-changed"));
};

export const setAccessToken = (token) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};
